import { ArrowLeft, ChevronDown, type LucideIcon } from "lucide-react";
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { Icon } from "../../icons/Icon";
import { Bdi } from "../../primitives/Bdi";
import { Button } from "../../primitives/Button";
import { DropdownMenu, type MenuItem } from "../../primitives/Menu";
import { useUiText } from "../../primitives/uiText";
import { cx, FOCUS_RING } from "../../styles";

/**
 * The top of every screen, and the two layouts under it.
 *
 * The header owns the actions. That is the handoff README's rule and it is
 * worth keeping: when a screen puts its primary action beside the table and the
 * next screen puts it in the header, somebody has to look for the New button
 * twice. One place, every time.
 */

/** One step in the trail the top bar renders. */
export interface Crumb {
	label: string;
	/** Absent for the last crumb, which is the page somebody is on. */
	to?: string;
}

interface BreadcrumbValue {
	trail: Crumb[];
	publish: (trail: Crumb[]) => void;
}

const BreadcrumbContext = createContext<BreadcrumbValue | null>(null);

/**
 * Carries the trail from the page that knows it to the top bar that draws it.
 *
 * A context rather than a prop, because the two are on opposite sides of the
 * shell: the top bar is mounted once, above the router, and the page that knows
 * it is "Inventory / Products / SKU-1042" is three levels down and remounts on
 * every navigation. Threading it as a prop would mean every route passing a
 * value it does not care about.
 *
 * It lives in `ui/` rather than in the shell so the design system stays
 * self-contained — the shell consumes this, not the other way round.
 */
export function BreadcrumbProvider({ children }: { children: ReactNode }) {
	const [trail, setTrail] = useState<Crumb[]>([]);
	const value = useMemo<BreadcrumbValue>(() => ({ trail, publish: setTrail }), [trail]);

	return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
}

/**
 * The current trail, for the top bar.
 *
 * Empty outside a provider rather than throwing: a page rendered on its own in
 * a test has no top bar to publish to, and failing for that would make every
 * screen's test assemble a shell.
 */
export function useBreadcrumb(): Crumb[] {
	return useContext(BreadcrumbContext)?.trail ?? [];
}

/**
 * Publishes a trail while a page is mounted, and clears it on the way out.
 *
 * The cleanup matters: without it the previous screen's trail stays in the top
 * bar for the moment between unmount and the next page publishing its own,
 * which reads as the breadcrumb lagging a click behind.
 */
export function usePublishBreadcrumb(trail: Crumb[] | undefined): void {
	const context = useContext(BreadcrumbContext);
	const publish = context?.publish;
	// Serialised, so a caller can pass a fresh array literal on every render
	// without re-publishing — which would be an infinite loop.
	const serialised = JSON.stringify(trail ?? []);

	useEffect(() => {
		if (publish === undefined || trail === undefined) {
			return;
		}
		publish(JSON.parse(serialised) as Crumb[]);
		return () => publish([]);
	}, [publish, serialised, trail]);
}

export interface PageHeaderProps {
	title: string;
	subtitle?: ReactNode;
	/**
	 * A back link. Mirrored in Arabic, because back is where the text came
	 * from. Absent on a detail page — a breadcrumb three or more deep — it is
	 * the nearest crumb above this page, labelled Back (bug list, line 7).
	 * Either way it returns to the screen somebody came from when there is one
	 * in this tab, and goes to this address when they opened the page directly.
	 */
	backTo?: string;
	backLabel?: string;
	/** A status chip beside the title. */
	chip?: ReactNode;
	/** Identifiers under the title — a SKU, an order number. Monospace, isolated. */
	meta?: string;
	/** The one thing this screen is for. */
	primaryAction?: ReactNode;
	/** Everything else, before it: free-form, always shown. Prefer `actions`. */
	secondaryActions?: ReactNode;
	/**
	 * The secondary actions as data, in priority order (ADJ-M03-1). The first
	 * `maxVisibleActions` are buttons and the rest go in a More menu; on a phone
	 * they all do. Ten equal outline buttons in a row is a header nobody can
	 * scan — this is what keeps a screen to one primary and two secondaries.
	 */
	actions?: PageAction[];
	/** How many of `actions` get a button of their own. Default 2. */
	maxVisibleActions?: number;
	/** Published to the top bar while this page is mounted. */
	breadcrumb?: Crumb[];
	className?: string;
}

/**
 * The header's back link (bug list, line 7). A click returns to the screen
 * somebody came from when this tab has been somewhere in the app before — the
 * router's first entry has the key "default" — and otherwise, like a middle or
 * modified click, follows the link to the parent.
 */
function BackLink({ to, label, history }: { to: string; label: string; history: boolean }) {
	const navigate = useNavigate();
	const location = useLocation();
	return (
		<Link
			to={to}
			onClick={(event) => {
				if (
					history &&
					location.key !== "default" &&
					event.button === 0 &&
					!event.metaKey &&
					!event.ctrlKey &&
					!event.shiftKey
				) {
					event.preventDefault();
					void navigate(-1);
				}
			}}
			className={cx(
				"inline-flex w-fit items-center gap-1.5 text-[12.5px] font-semibold text-fg-muted",
				"no-underline hover:text-fg",
				FOCUS_RING,
			)}
		>
			{/* Mirrored: back is the direction the text came from, which is the
			    right in Arabic. */}
			<Icon as={ArrowLeft} size={14} mirror />
			{label}
		</Link>
	);
}

/** One header action: a link (`to`) or a command (`onSelect`). */
export interface PageAction {
	/** Stable: the React key and the menu item id. */
	id: string;
	label: string;
	icon?: LucideIcon;
	/** Where it goes. Rendered as a real link, so it can be middle-clicked. */
	to?: string;
	onSelect?: () => void;
	danger?: boolean;
	disabled?: boolean;
}

export function PageHeader({
	title,
	subtitle,
	backTo,
	backLabel,
	chip,
	meta,
	primaryAction,
	secondaryActions,
	actions,
	maxVisibleActions = 2,
	breadcrumb,
	className,
}: PageHeaderProps) {
	usePublishBreadcrumb(breadcrumb);
	const t = useUiText();
	const hasActions = actions !== undefined && actions.length > 0;
	const parent =
		breadcrumb !== undefined && breadcrumb.length >= 3
			? [...breadcrumb.slice(0, -1)].reverse().find((crumb) => crumb.to !== undefined)?.to
			: undefined;
	const back = backTo ?? parent;

	return (
		<header className={cx("flex flex-col gap-3", className)}>
			{back !== undefined && (
				<BackLink
					to={back}
					label={backLabel ?? t("ui.actions.back")}
					// A named destination ("Back to dashboard") goes there: history
					// could be a drawer of this same page, such as an item's edit.
					history={backTo === undefined}
				/>
			)}

			<div className="flex flex-wrap items-start gap-3">
				{/* `basis-60`: below that width the actions wrap under the title
				    rather than squeezing it to a word per line on a phone
				    (ADJ-M01-1). */}
				<div className="flex min-w-0 flex-1 basis-60 flex-col gap-1">
					<div className="flex flex-wrap items-center gap-2">
						<h1 className="text-[24px] font-bold leading-tight tracking-[-0.015em] text-fg">
							{title}
						</h1>
						{chip}
					</div>
					{meta !== undefined && (
						// Isolated: an order number inside an Arabic subtitle reorders
						// without it, and a number nobody can read off the screen is a
						// number nobody can quote on the phone.
						<Bdi className="font-mono text-[12px] text-fg-subtle">{meta}</Bdi>
					)}
					{subtitle !== undefined && (
						<p className="text-[13px] leading-relaxed text-fg-muted">{subtitle}</p>
					)}
				</div>

				{(secondaryActions !== undefined || primaryAction !== undefined || hasActions) && (
					<div className="flex flex-wrap items-center gap-2">
						{secondaryActions}
						{hasActions && <PageActions actions={actions} max={maxVisibleActions} />}
						{primaryAction}
					</div>
				)}
			</div>
		</header>
	);
}

/** Below this width every secondary action goes in the More menu. */
const NARROW = "(max-width: 639px)";

/**
 * Whether the viewport is phone-narrow, following resizes. False where there
 * is no `matchMedia` (a test's DOM), which is the desktop layout.
 */
function useNarrow(): boolean {
	const [narrow, setNarrow] = useState(
		() => typeof window.matchMedia === "function" && window.matchMedia(NARROW).matches,
	);
	useEffect(() => {
		if (typeof window.matchMedia !== "function") {
			return;
		}
		const media = window.matchMedia(NARROW);
		const update = () => setNarrow(media.matches);
		media.addEventListener("change", update);
		return () => media.removeEventListener("change", update);
	}, []);
	return narrow;
}

/**
 * Which actions get a button and which go in the menu. Exported for its test.
 */
export function splitActions<T>(actions: T[], max: number, narrow: boolean) {
	const shown = narrow ? 0 : Math.max(0, max);
	return { buttons: actions.slice(0, shown), menu: actions.slice(shown) };
}

function PageActions({ actions, max }: { actions: PageAction[]; max: number }) {
	const t = useUiText();
	const navigate = useNavigate();
	const { buttons, menu } = splitActions(actions, max, useNarrow());

	const item = (action: PageAction): MenuItem => ({
		id: action.id,
		label: action.label,
		...(action.icon === undefined ? {} : { icon: action.icon }),
		...(action.danger === undefined ? {} : { danger: action.danger }),
		...(action.disabled === undefined ? {} : { disabled: action.disabled }),
		onSelect: () => {
			if (action.to !== undefined) {
				void navigate(action.to);
			}
			action.onSelect?.();
		},
	});

	return (
		<>
			{buttons.map((action) =>
				action.to !== undefined && action.disabled !== true ? (
					<Button key={action.id} asChild size="sm" variant="secondary" className="gap-1.5">
						<Link to={action.to}>
							{action.icon !== undefined && <Icon as={action.icon} size={14} />}
							{action.label}
						</Link>
					</Button>
				) : (
					<Button
						key={action.id}
						size="sm"
						variant={action.danger === true ? "danger" : "secondary"}
						{...(action.icon === undefined ? {} : { icon: action.icon })}
						{...(action.disabled === undefined ? {} : { disabled: action.disabled })}
						{...(action.onSelect === undefined ? {} : { onClick: action.onSelect })}
					>
						{action.label}
					</Button>
				),
			)}
			{menu.length > 0 && (
				<DropdownMenu
					label={t("ui.actions.more")}
					groups={[menu.map(item)]}
					trigger={
						<Button size="sm" variant="secondary" iconEnd={ChevronDown}>
							{t("ui.actions.more")}
						</Button>
					}
				/>
			)}
		</>
	);
}

export interface DetailLayoutProps {
	children: ReactNode;
	/** The summary column. Stacks under the content below 1024px. */
	rail?: ReactNode;
	className?: string;
}

/**
 * A record's content beside its summary.
 *
 * `data-detail-grid` is what stacks it below 1024px, and the rule is already in
 * `index.css` — the same reasoning as the table's card collapse: a breakpoint
 * belongs in CSS, where it is right on the first paint, rather than in a resize
 * listener that is wrong until the first measurement.
 *
 * The rail comes *after* the content in the DOM, which is what a screen reader
 * and a keyboard walk. A summary read before the thing it summarises is the
 * wrong order, and the visual placement is the grid's business.
 */
export function DetailLayout({ children, rail, className }: DetailLayoutProps) {
	if (rail === undefined) {
		return <div className={cx("flex flex-col gap-4", className)}>{children}</div>;
	}

	return (
		<div
			data-detail-grid="1"
			className={cx("grid gap-4", className)}
			style={{ gridTemplateColumns: "minmax(0, 1fr) 300px" }}
		>
			<div className="flex min-w-0 flex-col gap-4">{children}</div>
			<aside className="flex flex-col gap-4">{rail}</aside>
		</div>
	);
}

export interface KeyValueRow {
	label: string;
	value: ReactNode;
	/** Monospace: ids, SKUs, anything compared character by character. */
	mono?: boolean;
}

export interface KeyValueListProps {
	rows: KeyValueRow[];
	className?: string;
}

/**
 * A summary: labels and values, in a description list.
 *
 * A real `<dl>` rather than a grid of divs, because the pairing is the content.
 * A screen reader announces "Status, Active" from this and "Status Active" from
 * two spans — the difference between a summary and a run-on sentence.
 *
 * Every value is isolated. These are almost always identifiers and numbers,
 * which is exactly what reorders inside an Arabic label.
 *
 * The columns fit the space: one in a 300px rail, three or four across a
 * full-width card, where a single column left most of the card empty and made
 * a six-field summary a screen tall. A long identifier wraps rather than
 * pushing its column wider than the card.
 */
export function KeyValueList({ rows, className }: KeyValueListProps) {
	return (
		<dl
			className={cx(
				"grid grid-cols-[repeat(auto-fill,minmax(min(100%,240px),1fr))] gap-x-6 gap-y-2.5",
				className,
			)}
		>
			{rows.map((row) => (
				<div key={row.label} className="flex flex-col gap-0.5">
					<dt className="text-[11px] font-bold uppercase tracking-wide text-fg-subtle">
						{row.label}
					</dt>
					<dd
						className={cx(
							"text-[13px] text-fg [overflow-wrap:anywhere]",
							row.mono === true && "font-mono",
						)}
					>
						<Bdi>{row.value}</Bdi>
					</dd>
				</div>
			))}
		</dl>
	);
}
